import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaAnglesDown } from "react-icons/fa6";
import { getAllProducts, getCategoryAndSubcategoryProducts, getAProduct } from '../features/products/productSlice';
import { fetchCategories } from '../features/productCategory/productcateSlice';
import { addProdToCart, getUserCart, syncLocalCart } from '../features/user/userSlice';
import { base_url } from '../utils/axiosConfig';
import { MdKeyboardDoubleArrowUp } from "react-icons/md";
import { MdKeyboardArrowDown } from "react-icons/md";
import { CiSearch } from 'react-icons/ci';
import { FaFilterCircleXmark } from 'react-icons/fa6';
import ProductCard from '../components/ProductCard';
import ProductSpinner from '../components/ProductSpinner';
import 'bootstrap/dist/css/bootstrap.min.css';
import { IoClose } from "react-icons/io5";


const OurStore = () => {
  const [grid] = useState(4); // Grid setting for larger screens
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [visibleCount, setVisibleCount] = useState(16);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [mainImage, setMainImage] = useState('');
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [sortOption, setSortOption] = useState('all-products');
  const [searchQuery, setSearchQuery] = useState('');

  const productState = useSelector((state) => state.product.product);
  const { items: categories } = useSelector((state) => state.category);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const categoryId = searchParams.get('category');
  const subCategoryId = searchParams.get('subCategory');
  const searchTerm = searchParams.get('search')?.toLowerCase() || '';
  const getProductId = location.pathname.split('/')[2];

  const handleScroll = () => {
    setShowScrollTop(window.scrollY > 200);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleShowMore = () => {
    setVisibleCount((prev) => prev + 12);
  };

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setLoading(true);
    if (categoryId && subCategoryId) {
      dispatch(getCategoryAndSubcategoryProducts({ category: categoryId, subCategory: subCategoryId }))
        .then(() => setLoading(false));
    } else {
      dispatch(getAllProducts())
        .then(() => setLoading(false));
    }
    dispatch(fetchCategories());
  }, [dispatch, categoryId, subCategoryId]);

  useEffect(() => {
    if (getProductId) {
      dispatch(getAProduct(getProductId));
      if (isAuthenticated) dispatch(getUserCart());
    }
    setSearchQuery(searchTerm);
  }, [dispatch, getProductId, isAuthenticated, searchTerm]);

  useEffect(() => {
    if (productState?.images?.length > 0 && !Array.isArray(productState)) {
      setMainImage(`${base_url}${productState.images[0]?.url}`);
    }
  }, [productState]);

  const handleCategoryClick = (id) => {
    setExpandedCategory(expandedCategory === id ? null : id);
  };

  const handleSortChange = (e) => {
    setSortOption(e.target.value);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value.toLowerCase());
  };

  const handleClearFilter = () => {
    setExpandedCategory(null);
    const newParams = new URLSearchParams(location.search);
    newParams.delete('category');
    newParams.delete('subCategory');
    navigate({ search: newParams.toString() });
    dispatch(getAllProducts()).then(() => setLoading(false));
  };

  const uploadCart = (product, qty = 1) => {
    const cartItem = {
      ProductId: {
        _id: product?._id,
        title: product.title,
        images: product.images,
        price: product.CurrentPrice,
      },
      _id: Math.random().toString(36).substr(2, 9),
      quantity: qty,
      price: product.CurrentPrice,
    };

    if (isAuthenticated) {
      dispatch(addProdToCart(cartItem)).then(() => {
        dispatch(getUserCart());
        toast.success(`Added ${qty} ${product.title} to cart!`);
      });
    } else {
      const localCart = JSON.parse(localStorage.getItem('cart')) || [];
      const existingItemIndex = localCart.findIndex((item) => item.ProductId._id === product._id);
      if (existingItemIndex !== -1) {
        localCart[existingItemIndex].quantity += qty;
      } else {
        localCart.push(cartItem);
      }
      localStorage.setItem('cart', JSON.stringify(localCart));
      dispatch(syncLocalCart(localCart));
      toast.success(`Added ${qty} ${product.title} to cart!`);
    }
  };

  const filteredProducts = Array.isArray(productState)
    ? productState.filter((product) => {
      const matchesCategory = !categoryId || product.Category === categoryId;
      const matchesSubCategory = !subCategoryId || product.subCategory === subCategoryId;
      const matchesSearchQuery = product.title?.toLowerCase().includes(searchQuery);
      return matchesCategory && matchesSubCategory && matchesSearchQuery;
    })
    : [];

  const sortedProducts = filteredProducts.sort((a, b) => {
    switch (sortOption) {
      case 'all-products':
        return (b.sales || 0) - (a.sales || 0);
      case 'title-ascending':
        return String(a.title || '').localeCompare(String(b.title || ''));
      case 'title-descending':
        return String(b.title || '').localeCompare(String(b.title || ''));
      case 'price-ascending':
        return a.CurrentPrice - b.CurrentPrice;
      case 'price-descending':
        return b.CurrentPrice - a.CurrentPrice;
      default:
        return 0;
    }
  });

  return (
    <>
      <div className="store-wrapper py-3">
        <div className="container-fluid">
          <div className="row">
            {/* Sidebar for larger screens */}
            <div className="col-12 col-md-4 col-lg-3 mb-4 d-none d-md-block">
              <div className="filter-card">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="filter-title">Shop by Categories</h5>
                  {(categoryId || subCategoryId) && (
                    <div
                      onClick={handleClearFilter}
                      className="clear-filter"
                      title="Clear Filters"
                    >
                      <FaFilterCircleXmark size={16} />
                    </div>
                  )}
                </div>
                <ul className="list-unstyled">
                  {[...categories]
                    .sort((a, b) => String(a.title || '').localeCompare(String(b.title || '')))
                    .map((category) => (
                      <li key={category._id} className="mb-2">
                        <div
                          className={`category-btn ${category._id === categoryId ? 'active-category1' : ''}`}
                          onClick={() => handleCategoryClick(category._id)}
                        >
                          {category.title}
                          <span className={`toggle-icon ${expandedCategory === category._id ? 'expanded' : ''}`}>
                            <MdKeyboardArrowDown size={20} />
                          </span>
                        </div>
                        {expandedCategory === category._id && (
                          <ul className={`subcategory-list ${expandedCategory === category._id ? 'show' : ''}`}>
                            {category.subcategories?.length > 0 ? (
                              [...category.subcategories]
                                .sort((a, b) => String(a.title || '').localeCompare(String(b.title || '')))
                                .map((subCategory) => (
                                  <li key={subCategory._id}>
                                    <button
                                      onClick={() =>
                                        navigate(`?category=${category._id}&subCategory=${subCategory._id}`)
                                      }
                                      className={`subcategory-btn ${subCategory._id === subCategoryId ? 'active-subcategory1' : ''}`}
                                    >
                                      - {subCategory.title}
                                    </button>
                                  </li>
                                ))
                            ) : (
                              <li className="no-sub">No subcategories</li>
                            )}
                          </ul>
                        )}
                      </li>
                    ))}
                </ul>
              </div>
            </div>

            {/* Main Content */}
            <div className="col-12 col-md-8 col-lg-9">
              <div className="filter-sort-grid">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <div className="position-relative" style={{ flex: '1 1 150px', minWidth: '120px' }}>
                    <div
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '8px',
                        transform: 'translateY(-50%)',
                        display: 'flex',
                        alignItems: 'center',
                        height: '60%',
                        borderRight: '1px solid #ccc',
                        paddingRight: '6px',
                        color: '#888',
                      }}
                    >
                      <CiSearch />
                    </div>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Search products..."
                      value={searchQuery}
                      onChange={handleSearchChange}
                      style={{ paddingLeft: '35px' }}
                    />
                  </div>

                  <select
                    className="form-select form-select-sm"
                    onChange={handleSortChange}
                    style={{ flex: '0 0 140px', minWidth: '120px' }}
                  >
                    <option value="all-products">All Products</option>
                    <option value="title-ascending">A-Z</option>
                    <option value="title-descending">Z-A</option>
                    <option value="price-ascending">Price, low to high</option>
                    <option value="price-descending">Price, high to low</option>
                  </select>

                  <div className="col-12 mt-2 d-md-none d-flex align-items-center gap-2">
                    <button
                      className="btn filterbyCcatagory btn-outline-primary flex-grow-1 d-flex align-items-center justify-content-center gap-2"
                      onClick={toggleModal}
                    >
                      Filter by Category <FaFilterCircleXmark size={18} />
                    </button>

                    {(categoryId || subCategoryId) && (
                      <div
                        onClick={handleClearFilter}
                        className="clear-filter"
                        title="Clear Filters"
                        style={{ cursor: 'pointer' }}
                      >
                        <FaFilterCircleXmark size={14} /> Clear Filter
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Sliding Modal for Small Screens */}
              <div
                className={`modal-slide-in ${isModalOpen ? 'show' : ''}`}
                style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1050 }}
                onClick={toggleModal}
              >
                <div
                  className="modal-dialog modal-dialog-scrollable h-100 my-0 ms-0"
                  style={{ maxWidth: '260px' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="modal-content filter-card h-100">
                    <div className="modal-header border-0 py-2 d-flex justify-content-between align-items-center">
                      <h5 className="filter-title mb-0">Categories</h5>
                      <div
                        onClick={toggleModal}
                      >
                        <IoClose  />
                      </div>
                    </div>

                    <div className="modal-body p-2">
                      {(categoryId || subCategoryId) && (
                        <div
                          onClick={handleClearFilter}
                          className="clear-filter mb-2"
                          title="Clear Filters"
                        >
                          <FaFilterCircleXmark size={14} /> Clear Filter
                        </div>
                      )}
                      <ul className="list-unstyled">
                        {[...categories]
                          .sort((a, b) => String(a.title || '').localeCompare(String(b.title || '')))
                          .map((category) => (
                            <li key={category._id} className="mb-1">
                              <div
                                className={`category-btn ${category._id === categoryId ? 'active-category1' : ''}`}
                                onClick={() => handleCategoryClick(category._id)}
                              >
                                {category.title}
                                <span className={`toggle-icon ${expandedCategory === category._id ? 'expanded' : ''}`}>
                                  <MdKeyboardArrowDown size={18} />
                                </span>
                              </div>
                              {expandedCategory === category._id && (
                                <ul className={`subcategory-list ${expandedCategory === category._id ? 'show' : ''}`}>
                                  {category.subcategories?.length > 0 ? (
                                    [...category.subcategories]
                                      .sort((a, b) => String(a.title || '').localeCompare(String(b.title || '')))
                                      .map((subCategory) => (
                                        <li key={subCategory._id}>
                                          <button
                                            onClick={() => {
                                              navigate(`?category=${category._id}&subCategory=${subCategory._id}`);
                                              toggleModal();
                                            }}
                                            className={`subcategory-btn ${subCategory._id === subCategoryId ? 'active-subcategory1' : ''}`}
                                          >
                                            - {subCategory.title}
                                          </button>
                                        </li>
                                      ))
                                  ) : (
                                    <li className="no-sub">No subs</li>
                                  )}
                                </ul>
                              )}
                            </li>
                          ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              {isModalOpen && <div className="modal-backdrop fade show" />}

              {loading ? (
                <ProductSpinner />
              ) : (
                <div className="row row-cols-2 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 g-2 mt-1">
                  {sortedProducts.length > 0 ? (
                    sortedProducts.slice(0, visibleCount).map((product) => (
                      <ProductCard
                        key={product._id}
                        data={product}
                        grid={grid}
                        onAddToCart={uploadCart}
                      />
                    ))
                  ) : (
                    <div className="col-12">
                      <p className="text-center text-muted">
                        No products found for the selected category and subcategory.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {visibleCount < sortedProducts.length && !loading && (
                <div className="d-flex justify-content-center mt-4">
                  <button
                    className="btn btn-outline-primary px-4 d-flex align-items-center gap-2"
                    onClick={handleShowMore}
                  >
                    More Products <FaAnglesDown />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showScrollTop && (
        <div
          className="scroll-to-top"
          onClick={scrollToTop}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-6px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          Back to top <MdKeyboardDoubleArrowUp size={18} />
        </div>
      )}
    </>
  );
};

export default OurStore;